/**
 * Timezone utility functions for scheduled posts
 */

/**
 * Default timezone for the application
 */
const DEFAULT_TIMEZONE = 'Asia/Nicosia';

/**
 * Get the user's timezone - defaults to Asia/Nicosia
 * @returns IANA timezone string
 */
export function getUserTimezone(): string {
  return DEFAULT_TIMEZONE;
}

/**
 * Format a timestamp in a specific timezone with full details (24-hour format)
 * @param timestamp Unix timestamp in milliseconds
 * @param timezone IANA timezone string
 * @returns Formatted string like "Jan 15, 2026, 15:00 EET"
 */
export function formatScheduledTime(timestamp: number, timezone: string): string {
  const date = new Date(timestamp);
  return date.toLocaleString('en-GB', {
    timeZone: timezone,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
    hour12: false,
  });
}

/**
 * Get relative time until a future timestamp
 * @param scheduledFor Unix timestamp in milliseconds
 * @returns Relative time string like "in 2 hours" or "Past scheduled time"
 */
export function getTimeUntilPublish(scheduledFor: number): string {
  const now = Date.now();
  const diff = scheduledFor - now;

  if (diff < 0) return 'Past scheduled time';

  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `in ${days} ${days === 1 ? 'day' : 'days'}`;
  if (hours > 0) return `in ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  if (minutes > 0) return `in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  return 'Publishing soon';
}

/**
 * Get timezone abbreviation from IANA timezone
 * @param timezone IANA timezone string
 * @returns Timezone abbreviation like "EET" or "EEST"
 */
export function getTimezoneAbbreviation(timezone: string): string {
  const date = new Date();
  const formatted = date.toLocaleString('en-US', {
    timeZone: timezone,
    timeZoneName: 'short',
  });

  // Extract timezone abbreviation from formatted string
  const match = formatted.match(/([A-Z]{3,5})$/);
  return match ? match[1] : timezone;
}

/**
 * Get current time in HH:MM format (24-hour)
 * @param timezone IANA timezone string
 * @returns Time string like "15:30"
 */
export function getCurrentTime(timezone: string = DEFAULT_TIMEZONE): string {
  const now = new Date();
  const formatted = now.toLocaleString('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  // Extract just the time part (HH:MM)
  return formatted.split(', ')[1] || formatted;
}

/**
 * Convert local date and time to UTC timestamp
 * @param date Date object in local timezone
 * @param timeString Time string in HH:MM format
 * @returns UTC timestamp in milliseconds
 */
export function localDateTimeToUTC(date: Date, timeString: string): number {
  const [hours, minutes] = timeString.split(':').map(Number);
  const scheduledDateTime = new Date(date);
  scheduledDateTime.setHours(hours, minutes, 0, 0);
  return scheduledDateTime.getTime();
}

/**
 * Validate that a timestamp is at least a certain number of minutes in the future
 * @param timestamp Unix timestamp in milliseconds
 * @param minMinutes Minimum minutes in future (default: 1)
 * @returns true if valid, false otherwise
 */
function isValidFutureTime(timestamp: number, minMinutes: number = 1): boolean {
  const minFutureTime = Date.now() + minMinutes * 60 * 1000;
  return timestamp >= minFutureTime;
}

/**
 * Get human-readable error message for invalid scheduled time
 * @param timestamp Unix timestamp in milliseconds
 * @param minMinutes Minimum minutes in future (default: 1)
 * @returns Error message or null if valid
 */
export function getScheduleTimeError(timestamp: number, minMinutes: number = 1): string | null {
  if (timestamp < Date.now()) {
    return 'Scheduled time must be in the future';
  }

  if (!isValidFutureTime(timestamp, minMinutes)) {
    return `Scheduled time must be at least ${minMinutes} ${minMinutes === 1 ? 'minute' : 'minutes'} in the future`;
  }

  return null;
}
